import React, { useState, useCallback, useEffect, useRef } from "react";

type AriaLiveProp = { "aria-live"?: React.AriaAttributes["aria-live"] };

type AriaLiveRegionProps = {
    message: string;
} & AriaLiveProp;

const DEFAULT_ARIA_LIVE: React.AriaAttributes["aria-live"] = "polite";

export const AriaLiveRegion = ({ message, "aria-live": ariaLive = DEFAULT_ARIA_LIVE }: AriaLiveRegionProps) => (
    <div
        style={{
            border: 0,
            clip: "rect(0 0 0 0)",
            height: "1px",
            margin: "-1px",
            overflow: "hidden",
            padding: 0,
            width: "1px",
            position: "absolute",
        }}
        role="alert"
        aria-live={ariaLive}
        aria-relevant="additions"
        aria-atomic="true"
    >
        {message}
    </div>
);

export const useAnnouncement = (initialMessage = "") => {
    const [announcement, setAnnouncement] = useState(initialMessage);
    const animationFrameRef = useRef<number | null>(null);

    // Add cleanup so that setAnnouncement can't be called if unmounted
    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    // Note: Use requestAnimationFrame to ensure the DOM processes the clearing of the announcement
    // before setting the new message, allowing screen readers to re-announce the same message
    const announce = useCallback((message: string) => {
        setAnnouncement("");
        animationFrameRef.current = requestAnimationFrame(() => {
            setAnnouncement(message);
        });
    }, []);

    const AnnouncementRegion = useCallback(
        ({ "aria-live": ariaLive = "polite" }: { "aria-live"?: React.AriaAttributes["aria-live"] }) => (
            <AriaLiveRegion message={announcement} aria-live={ariaLive} />
        ),
        [announcement]
    );

    return { announce, AnnouncementRegion };
};
